// Créneaux communs, meilleur prochain créneau, alerte de changement d’heure.
import { MIN, STEP, bounds, info, nextDate, todayIn } from './time';
import { working, label, placeOf, type Contact, type Hours } from './contact';

export interface Slot {
  start: number;
  end: number;
  people: Contact[];
}

/** L’appel tient-il entièrement dans les horaires, minute par minute (nuit et changements d’heure compris) ? */
export function meetingFits(h: Hours, start: number, duration: number): boolean {
  for (let m = 0; m < duration; m++) if (!working(h, start + m * MIN)) return false;
  return true;
}

export interface CandidateOptions {
  people: Contact[];
  /** Date civile dans le fuseau `tz`. */
  date: string;
  /** Durée en minutes. */
  duration: number;
  /** Mes horaires, ou null pour ne pas les imposer. */
  me: Hours | null;
  /** Fuseau de référence de la journée balayée. */
  tz: string;
  /** Tout le monde doit être disponible (sinon, au moins une personne). */
  allRequired: boolean;
  now: number;
}

/** Tous les créneaux de la journée, par pas de 15 min, à partir de maintenant si la journée est en cours. */
export function meetingCandidates(o: CandidateOptions): Slot[] {
  const day = bounds(o.date, o.tz);
  const first = o.now < day.end ? Math.max(day.start, Math.ceil(o.now / STEP) * STEP) : day.start;
  const out: Slot[] = [];
  if (!o.people.length) return out;
  for (let t = first; t < day.end; t += STEP) {
    if (o.me && !meetingFits(o.me, t, o.duration)) continue;
    const people = o.people.filter((c) => meetingFits(c, t, o.duration));
    if (people.length && (o.allRequired ? people.length === o.people.length : true)) {
      out.push({ start: t, end: t + o.duration * MIN, people });
    }
  }
  return out;
}

export interface BestNext {
  slot: Slot;
  date: string;
  /** 0 = aujourd’hui, 1 = demain… */
  daysAhead: number;
}

/**
 * Meilleur prochain créneau : d’abord aujourd’hui, puis les jours suivants (jusqu’à `lookAhead`).
 * Le soir, la réponse est « demain 9h », jamais « pas de créneau ».
 */
export function bestNext(o: { people: Contact[]; me: Hours; now: number; date?: string; duration?: number; lookAhead?: number }): BestNext | null {
  if (!o.people.length) return null;
  const duration = o.duration ?? 30, lookAhead = o.lookAhead ?? 7;
  const today = todayIn(o.me.tz, o.now);
  const base = o.date ?? today;
  const pick = (options: Slot[]): Slot | undefined =>
    options.sort((a, b) => b.people.length - a.people.length || a.start - b.start)[0];
  let best = pick(meetingCandidates({ people: o.people, date: base, duration, me: o.me, tz: o.me.tz, allRequired: false, now: o.now }));
  if (best) return { slot: best, date: base, daysAhead: 0 };
  if (base < today) return null;
  for (let k = 1; k <= lookAhead; k++) {
    const d = nextDate(base, k);
    best = pick(meetingCandidates({ people: o.people, date: d, duration, me: o.me, tz: o.me.tz, allRequired: false, now: o.now }));
    if (best) return { slot: best, date: d, daysAhead: k };
  }
  return null;
}

export interface DstAlert {
  /** Instant approximatif (à la journée près) du changement. */
  when: number;
  meFrom: number;
  meTo: number;
  moved: Array<{ id: string; name: string; place: string; from: number; to: number }>;
}

/** Prochain basculement été/hiver, dans les `days` prochains jours, qui modifie mon fuseau ou mon écart avec un contact. */
export function dstAhead(o: { me: Hours; contacts: Contact[]; now: number; days?: number }): DstAlert | null {
  const days = o.days ?? 7;
  const meNow = info(o.me.tz, o.now).offset;
  const delta = new Map(o.contacts.map((c) => [c.id, info(c.tz, o.now).offset - meNow]));
  for (let d = 1; d <= days; d++) {
    const t = o.now + d * 86_400_000, me2 = info(o.me.tz, t).offset;
    const moved = o.contacts.filter((c) => info(c.tz, t).offset - me2 !== delta.get(c.id));
    if (me2 !== meNow || moved.length) {
      return {
        when: t, meFrom: meNow, meTo: me2,
        moved: moved.map((c) => ({ id: c.id, name: label(c), place: placeOf(c), from: delta.get(c.id) ?? 0, to: info(c.tz, t).offset - me2 })),
      };
    }
  }
  return null;
}
